import { FC, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';

export const LoginPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuth();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login(formData.email, formData.password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '登录失败，请重试');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="min-h-screen bg-apple-light-gray flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-[400px]">

        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-apple-dark rounded-apple flex items-center justify-center">
              <span className="text-white font-bold">白</span>
            </div>
            <span
              className="text-xl font-semibold text-apple-dark"
              style={{ letterSpacing: '-0.02em' }}
            >
              白茆AI
            </span>
          </Link>
          <h1
            className="font-bold text-apple-dark mb-1.5"
            style={{ fontSize: '1.75rem', letterSpacing: '-0.025em' }}
          >
            欢迎回来
          </h1>
          <p className="text-[15px] text-apple-gray">请登录您的账户继续使用</p>
        </div>

        {/* 表单卡片 */}
        <div className="bg-white rounded-apple-xl shadow-apple-md p-8">
          <form onSubmit={handleSubmit} className="space-y-5">

            {/* 邮箱 */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-apple-dark mb-1.5">
                邮箱地址
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="input-apple"
                placeholder="name@example.com"
              />
            </div>

            {/* 密码 */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-apple-dark mb-1.5">
                密码
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="input-apple"
                placeholder="输入密码"
              />
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="flex items-start gap-2.5 p-3.5 rounded-apple bg-[#FFF2F0] border border-[#FFCCC7]">
                <svg className="w-4 h-4 text-apple-red flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-apple-red">{error}</p>
              </div>
            )}

            {/* 提交 */}
            <Button type="submit" className="w-full justify-center" disabled={isLoading} size="lg">
              {isLoading ? '登录中…' : '登录'}
            </Button>
          </form>

          {/* 分隔线 */}
          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-apple-separator" />
            <span className="text-sm text-apple-gray">或者</span>
            <div className="flex-1 h-px bg-apple-separator" />
          </div>

          {/* 注册入口 */}
          <p className="text-center text-[15px] text-apple-gray">
            还没有账户？{' '}
            <Link to="/register" className="text-apple-blue hover:underline font-medium">
              立即注册
            </Link>
          </p>
        </div>

        {/* 底部辅助链接 */}
        <div className="mt-6 flex flex-col items-center gap-3">
          <Link to="/contact" className="text-sm text-apple-gray hover:text-apple-dark transition-colors">
            遇到问题？联系客服
          </Link>
          <p className="text-xs text-apple-gray text-center">
            登录即代表同意{' '}
            <a href="#" className="text-apple-blue hover:underline">服务条款</a>
            {' '}和{' '}
            <a href="#" className="text-apple-blue hover:underline">隐私政策</a>
          </p>
        </div>
      </div>
    </div>
  );
};
